import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { EventTypes, MULTIPANE_FULLSCREEN_MODE_ID, ViewType } from '../../../assets/constants'
import WidgetDashboard from '../../../components/widgetDashboard/widgetDashboard'
import { useStyles } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { setActiveLayout } from '../../../reducer/home.reducer'
import { selectPrimarySecurity } from '../../../selectors/home'
import { selectActiveWidget, selectAllLayouts } from '../../../selectors/widgetDashboard'
import { AppContext } from '../../../App'
import useSelectActiveLayout from '../../../hooks/useSelectActiveLayout'
import { WidgetTypes } from '../../../components/widgetDashboard/widgetDashboard.enums'
import { getExchangeAndCodeFromKey, getSecurityAsync, getSecurityKey } from '../../../util/security'
import { selectActiveChartId, selectCalendarEvents, selectChartConfigs, selectDrawings } from '../../../selectors/chart'
import { ChartDrawingItem, DrawingDisplayType, DrawingTypes } from '../../../components/chartV2/drawingTools/drawing.interface'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import { selectSnapshots } from '../../../selectors/security'
import { fetchSecuritySnapshotsBulk } from '../../../services/apis/candles'
import { WebSocketRequestMessage_Subscription } from '../../../proto/generated/websocket'
import websocketActions from '../../../actions/websocket.actions'
import HomeLayout from '../../homeLayout'
import { Paper, useTheme } from '@mui/material'
import eventBus from '../../../events/eventBus'
import { SecurityClickEventData, SecurityClickSource } from '../../../events/eventTypings'
import useSetPrimarySecurity from '../../../hooks/useSetPrimarySecurity'
import { openChartInNewWindow } from '../../../util/common'
import { selectDefaultLayoutId } from '../../../selectors/user'
import ChartFooter from '../../../components/chartV2/chartFooter/chartFooter'
import { useIsStaleHook } from '../../../hooks/useIsStaleHook'
import StaleDataModal from '../../../components/staleDataModal/staleDataModal'

import { fetchUserDashboardById } from '../../../services/apis/dashboard'
import { updateLayout } from '../../../reducer/widgetDashboard.reducer'
import AccessDenied from '../../userStatusPage/accessDenied'
import { updateUserDashboard } from '../../../reducer/user.reducer'
import { parseErrorFromArrayBuffer } from '../../../components/common/errorParser'
import { ErrorCodes, Exchange } from '../../../proto/generated/common'
import { selectIsEmbedded, selectIsMobile } from '../../../selectors/device'
import commonActions from '../../../actions/common.actions'
import { Platform } from '../../../proto/generated/user-preference'
import { selectUserDashboards } from '../../../selectors/layout'
import { useChartConfigs } from '../../../hooks/useChartConfigs'
import { getSavedLayout } from '../../../util/widgetDashboard'
import { sortKeys } from '../../../util/general'
import { callNative } from '../../../util/nativeBridge'

export type CashViewProps = {
    view: ViewType
}

const CashView: React.FC<CashViewProps> = ({ view }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const theme = useTheme()
    const mounted = useRef(false)
    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)
    const params = useParams()
    const urlLayoutId = params.layoutId || ''
    const allLayouts = useSelector(selectAllLayouts)
    const calendarEvents = useSelector(selectCalendarEvents)
    const isMobile = useSelector(selectIsMobile)
    const defaultLayoutId = useSelector(selectDefaultLayoutId(isMobile ? Platform.MOBILE : Platform.DESKTOP))
    const [activeLayoutId] = useSelectActiveLayout()
    const activeLayout = allLayouts[activeLayoutId]
    const setPrimarySecurity = useSetPrimarySecurity()
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId || ''))
    const { key, expressionMode, primary, expressionSecurities, displayName, underlyingSecurityCode } = useSelector(selectPrimarySecurity)
    const securitySnapshotsObject = useSelector(selectSnapshots)
    const { classes } = useStyles()
    const previousSecurityString = useRef<string>('')
    const previousLayoutId = useRef('')
    const previousActiveLayoutId = useRef('')
    const previousKey = useRef(activeWidget?.settings?.key || '')
    const isStale = useIsStaleHook(5, primary?.exchange ?? Exchange.NSE)
    const [showStaleModal, setShowStaleModal] = useState(false)
    const isEmbedded = useSelector(selectIsEmbedded)
    const prevLayoutRef = useRef<string | null>(null);
    const layoutPlatformCache = useRef<Record<string, Platform>>({});
    const fetchInProgressRef = useRef<string | null>(null);
    const dashboards = useSelector(selectUserDashboards);
    const configs = useSelector(selectChartConfigs)
    const chartConfigs = useChartConfigs(activeLayoutId, activeLayout, configs)
    const drawings: Record<string, ChartDrawingItem[]> = useSelector(selectDrawings);
    const [selectedSecurities, setSelectedSecurities] = useState<string[]>([]);

    useEffect(() => {
        if (activeChartId) {
            callNative('setActiveChart', activeChartId);
        }
    }, [activeChartId])

    useEffect(() => {
        if (!isEmbedded || (!primary && !expressionSecurities.length) || !key) return;
        callNative('onPrimarySecurityChange', JSON.stringify({
            key,
            displayName,
            expressionMode,
            securities: (expressionSecurities.length? expressionSecurities : [primary]).map(v => getSecurityKey(v.exchange, v.code)),
            underlyingSecurity: underlyingSecurityCode
        }))
    }, [isEmbedded, key, expressionMode, primary, expressionSecurities, displayName, underlyingSecurityCode])

    useEffect(() => {
        if (!isEmbedded) return;
        function onOrientationChange() {
            if (!activeLayout) return;
            const landscape = window.matchMedia("(orientation: landscape)");
            const root = activeLayout.panes

            if (!root || !root.panes.length) return;

            dispatch(updateLayout({
                id: activeLayoutId,
                layout: {
                    ...activeLayout,
                    panes: {
                        ...activeLayout.panes,
                        horizontal: landscape.matches
                    }
                }
            }))
        }

        screen.orientation.addEventListener("change", onOrientationChange);

        return () => {
            screen.orientation.removeEventListener("change", onOrientationChange);
        }

    }, [activeLayout, activeLayoutId, isEmbedded])

    const userDashboardString = useMemo(() => dashboards.find((d) => d.id === activeLayoutId)?.layout ?? "", [dashboards, activeLayoutId])
    const currentLayoutString = useMemo(() => {
        return JSON.stringify(sortKeys(getSavedLayout(activeLayoutId, activeLayout, chartConfigs, drawings)))
    }, [activeLayout, dashboards, activeLayoutId, chartConfigs, drawings, isEmbedded])


    useEffect(() => {
        if (!isEmbedded || !activeLayoutId) return;
        const changed = userDashboardString !== currentLayoutString;
        callNative('isLayoutChanged', changed ? "true" : "false");
        if (changed) {
            callNative('onLayoutChange', activeLayoutId, currentLayoutString)
        }
    }, [userDashboardString, currentLayoutString, activeLayoutId, isEmbedded]);


    useEffect(() => {
        isStale && setShowStaleModal(true)
    }, [isStale])

    // const onStaleModalClose = useCallback(() => setShowStaleModal(false), [])
    const [accessDenied, setAccessDenied] = useState(false)

    const disableTimeRangeOptions = useMemo(() => {
        return !(activeWidget?.widgetType === WidgetTypes.CHART || activeWidget?.widgetType === WidgetTypes.MULTI_STRIKE_CHART)
    }, [activeWidget])

    const disableDrawingOptions = useMemo(() => {
        return !(activeWidget?.widgetType === WidgetTypes.CHART)
    }, [activeWidget])

    const securityClickHandler = useCallback(
        (event: CustomEvent<SecurityClickEventData>) => {
            const { detail: data } = event
            if (data.source === SecurityClickSource.WATCHLIST) {
                setPrimarySecurity(data.key, data.securities, data.expressionMode)
            } else {
                openChartInNewWindow(data.key)
            }
        },
        [setPrimarySecurity]
    )

    useEffect(() => {
        if (activeLayoutId && activeLayout && activeLayoutId !== previousActiveLayoutId.current && activeLayout.widgets && Object.keys(activeLayout.widgets).length) {
            previousActiveLayoutId.current = activeLayoutId
            let cancelled = false
            ;(async () => {
                const allWidgetSecurities = Object.values(activeLayout.widgets)
                    .flatMap((widget) => widget?.settings?.securities ?? [])
                    .filter((sec): sec is string => !!sec)
                const uniqueSecurities = Array.from(new Set(allWidgetSecurities))
                const cacheResults = await Promise.all(uniqueSecurities.map((sec) => getSecurityAsync(sec)))
                const securitiesToFetch = uniqueSecurities.filter((sec, index) => !cacheResults[index])
                if (!cancelled && securitiesToFetch.length) {
                    fetchSecuritySnapshotsBulk({
                        securities: securitiesToFetch.map((token) => {
                            const [exchange, code] = getExchangeAndCodeFromKey(token)
                            return { exchange, code }
                        }),
                        dispatch: dispatch
                    })
                }
            })()
            return () => { cancelled = true }
        }
    }, [activeLayoutId, activeLayout, securitySnapshotsObject, defaultLayoutId, allLayouts])

    useEffect(() => {
        const initializeLayout = async () => {
            if (!defaultLayoutId || !Object.keys(allLayouts).length) return;
            const cachedPlatform = layoutPlatformCache.current[urlLayoutId ?? ''];

            const isPlatformIncompatible = (platform: Platform) => {
                return (isMobile && platform !== Platform.MOBILE) || (!isMobile && platform === Platform.MOBILE);
            };

            if (urlLayoutId === prevLayoutRef.current && cachedPlatform && !isPlatformIncompatible(cachedPlatform)) {
                return;
            }

            if (fetchInProgressRef.current === urlLayoutId) {
                return;
            }

            if (urlLayoutId) {
                // Fast path: layout already parsed in widgetDashboard store.
                const cachedLayout = allLayouts[urlLayoutId]
                const cachedDashboard = dashboards.find((d) => d.id === urlLayoutId);
                if (cachedLayout && cachedDashboard) {
                    const layoutPlatform = cachedDashboard.platform;
                    if (isPlatformIncompatible(layoutPlatform)) {
                        commonActions.setApiError(dispatch, {
                            error: true,
                            message: 'This layout is not compatible with your device.',
                            errorType: 'error'
                        });
                        navigate(generatePath(ROUTES.chart, { layoutId: defaultLayoutId }), { replace: true });
                        return;
                    }

                    layoutPlatformCache.current[cachedDashboard.id] = layoutPlatform;
                    prevLayoutRef.current = cachedDashboard.id;
                    dispatch(setActiveLayout(cachedDashboard.id));
                    return;
                }

                // Next fallback: dashboard exists in Redux user store but parsed layout isn't in widgetDashboard yet.
                if (cachedDashboard) {
                    const layoutPlatform = cachedDashboard.platform;
                    if (isPlatformIncompatible(layoutPlatform)) {
                        commonActions.setApiError(dispatch, {
                            error: true,
                            message: 'This layout is not compatible with your device.',
                            errorType: 'error'
                        });
                        navigate(generatePath(ROUTES.chart, { layoutId: defaultLayoutId }), { replace: true });
                        return;
                    }

                    layoutPlatformCache.current[cachedDashboard.id] = layoutPlatform;
                    prevLayoutRef.current = cachedDashboard.id;
                    dispatch(setActiveLayout(cachedDashboard.id));
                    dispatch(updateUserDashboard(cachedDashboard));

                    try {
                        const parsedLayout = JSON.parse(cachedDashboard.layout);
                        dispatch(
                            updateLayout({
                                id: cachedDashboard.id,
                                layout: parsedLayout
                            })
                        );
                    } catch (e) {
                        console.error('Failed to parse cached dashboard layout', e);
                    }

                    return;
                }

                fetchInProgressRef.current = urlLayoutId;
                try {
                    const fetchedLayout = await fetchUserDashboardById({ LayoutId: urlLayoutId });
                    const layoutPlatform = fetchedLayout.platform;

                    if (isPlatformIncompatible(layoutPlatform)) {
                        commonActions.setApiError(dispatch, {
                            error: true,
                            message: 'This layout is not compatible with your device.',
                            errorType: 'error'
                        });
                        navigate(generatePath(ROUTES.chart, { layoutId: defaultLayoutId }), { replace: true });
                        return;
                    }
                    layoutPlatformCache.current[fetchedLayout.id] = layoutPlatform;
                    prevLayoutRef.current = fetchedLayout.id;
                    dispatch(setActiveLayout(fetchedLayout.id))
                    dispatch(updateUserDashboard(fetchedLayout))
                    const parsedLayout = JSON.parse(fetchedLayout.layout)
                    dispatch(
                        updateLayout({
                            id: fetchedLayout.id,
                            layout: parsedLayout
                        })
                    )
                } catch (err: any) {
                    const { errorCode, message } = parseErrorFromArrayBuffer(err)
                    if (errorCode === ErrorCodes.ENTITY_NOT_FOUND) {
                        navigate(generatePath(ROUTES.chart, { layoutId: defaultLayoutId }), { replace: true })
                    } else if (errorCode === ErrorCodes.ACCESS_DENIED) {
                        setAccessDenied(true)
                    } else {
                        navigate(generatePath(ROUTES.chart, { layoutId: defaultLayoutId }), { replace: true })
                        console.error('Unhandled error:', ErrorCodes[errorCode ?? 0], message)
                    }
                } finally {
                    if (fetchInProgressRef.current === urlLayoutId) {
                        fetchInProgressRef.current = null;
                    }
                }
                return
            }
            if (!urlLayoutId) {
                navigate(generatePath(ROUTES.chart, { layoutId: defaultLayoutId }), { replace: true })
            }
        }
        initializeLayout()
        eventBus.subscribe(EventTypes.SECURITY_CLICKED, securityClickHandler)
        return () => {
            eventBus.unsubscribe(EventTypes.SECURITY_CLICKED, securityClickHandler)
        }
    }, [urlLayoutId, defaultLayoutId, allLayouts, dispatch, navigate, securityClickHandler, isMobile, dashboards])

    useEffect(() => {
        const fetchSecurities = async () => {
            if (!activeLayout?.widgets) {
                setSelectedSecurities([])
                return
            }
            const allWidgetSecurities = Object.values(activeLayout.widgets)
                .flatMap((widget) => widget?.settings?.securities ?? [])
                .filter((sec): sec is string => !!sec)
            const uniqueSecurities = Array.from(new Set(allWidgetSecurities))
            const cacheResults = await Promise.all(uniqueSecurities.map((sec) => getSecurityAsync(sec)))
            setSelectedSecurities(uniqueSecurities.filter((sec, index) => !!cacheResults[index]))
        }
        fetchSecurities()
    }, [activeLayout])

    useEffect(() => {
        //subscribe for new security list
        const securityString = Array.from(new Set([...selectedSecurities]))
            .sort()
            .join('__')
        if (previousSecurityString.current !== securityString && selectedSecurities.length) {
            const referencedSecurities: string[] = []
            // fetch any missing securities, works for fetching underlying when primary is FnO
            const securitiesToFetch: string[] = []
            selectedSecurities.forEach((v) => {
                const sn = securitySnapshotsObject[v]
                if (!sn || !sn.previousClose) {
                    securitiesToFetch.push(v)
                    return
                }
                if (sn.referencedSecurity && sn.referencedSecurity.exchange) {
                    const refKey = getSecurityKey(sn.referencedSecurity.exchange, sn.referencedSecurity.code)
                    if (!securitySnapshotsObject[refKey] || !securitySnapshotsObject[refKey].previousClose) {
                        referencedSecurities.push(refKey)
                    }
                }
            })

            const payload: Partial<WebSocketRequestMessage_Subscription> = {
                securitySnapshotSubscriptions: [
                    {
                        tag: 'primary-security',
                        securities: selectedSecurities.concat(referencedSecurities).map((v) => {
                            const [exchange, code] = getExchangeAndCodeFromKey(v)
                            return {
                                exchange,
                                code
                            }
                        })
                    }
                ]
            }
            websocketActions.setWebsocketSubscribeList(dispatch, payload)
            previousSecurityString.current = securityString
            securitiesToFetch.concat(referencedSecurities).length &&
                fetchSecuritySnapshotsBulk({
                    securities: securitiesToFetch.concat(referencedSecurities).map((v) => {
                        const [exchange, code] = getExchangeAndCodeFromKey(v)
                        return {
                            exchange,
                            code
                        }
                    }),
                    dispatch: dispatch
                }).catch((e) => {
                    console.error(e)
                })
        }
    }, [selectedSecurities, key, securitySnapshotsObject])

    useEffect(() => {
        if (urlLayoutId && previousLayoutId.current !== urlLayoutId && mounted.current) {
            previousLayoutId.current = urlLayoutId
            dispatch(setActiveLayout(urlLayoutId))
        }
    }, [urlLayoutId])

    useEffect(() => {
        if (
            (primary && previousKey.current !== getSecurityKey(primary?.exchange, primary?.code))
            || (expressionMode && expressionSecurities.length && previousKey.current !== key)
        ) {
            if (appContext.dashboard) {
                appContext.dashboard.updateSecurity(key, expressionMode, expressionMode ? expressionSecurities : primary ? [primary] : [], displayName)
                previousKey.current = key
            }
        }
    }, [key, primary, expressionSecurities, expressionMode, displayName])

    const onDrawingClick = (drawingType: DrawingTypes, options: Record<string, any>, enter?: boolean) => {
        if (!activeLayout) return
        const { widgets } = activeLayout
        Object.values(widgets).forEach((w) => {
            if (w?.widgetType === WidgetTypes.CHART) {
                const {
                    settings: { chartId }
                } = w
                if (enter) {
                    appContext.getChart(chartId).drawingManager.addDrawing(drawingType, DrawingDisplayType.DRAWING, options, {})
                } else {
                    appContext.getChart(chartId).drawingManager.abort()
                }
            }
        })
    }
    if (accessDenied) {
        return <AccessDenied />
    }

    return (
        <HomeLayout view={view} error={''}>
            <div
                className={classes.container}
                id={MULTIPANE_FULLSCREEN_MODE_ID}
                style={{
                    backgroundColor: theme.palette.background.default
                }}
            >
                <div className={classes.dashboardArea}>
                    <WidgetDashboard id={activeLayoutId || ''} allowInitialEmpty={false} maxPanes={isMobile ? 2 : undefined} />
                </div>
                {!isEmbedded && <Paper className={classes.footer}>
                    <ChartFooter activeChartId={activeChartId} disableTimeRange={disableTimeRangeOptions} disableDrawings={disableDrawingOptions} onDrawingClick={onDrawingClick} />
                </Paper>}
            </div>
        </HomeLayout>
    )
}

export default memo(CashView)
